import { useTableActionContext } from '@aibees/elephant/context';
import { StyledActionMenuItem } from '@aibees/elephant/feature/search-table-company/components/search-table-header/styles';
import { SearchModality } from '@aibees/elephant/feature/search-table-company/hooks/types';
import { useSearchModality } from '@aibees/elephant/hooks';
import { LabelType } from '@aibees/elephant/types/label';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Button, Menu, Stack, Tooltip } from '@mui/material';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { SearchTableActionRemoveLabelDialog } from '../search-table-action-remove-label-dialog';
import { SearchTableActionAddListDialog } from './components/search-table-action-add-list-dialog/search-table-action-add-list-dialog';
import * as SX from './sx';
import type { SearchTableActionDialogListsProps as Props } from './types';

export const SearchTableActionDialogLists = memo(
  ({ className, table, disabled, isSmall }: Props) => {
    const { listDialogState, removeListDialogState, ids } =
      useTableActionContext();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { modality } = useSearchModality();
    const isNewTab = useMemo(
      () => modality === SearchModality.UNSAVED,
      [modality]
    );

    // Memoize open calculation
    const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

    // Memoize event handlers
    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
      },
      []
    );

    const handleClose = useCallback(() => {
      setAnchorEl(null);
    }, []);

    const handleAddListClick = useCallback(() => {
      listDialogState.onTrue();
      handleClose();
    }, [listDialogState, handleClose]);

    const handleRemoveListClick = useCallback(() => {
      removeListDialogState.onTrue();
      handleClose();
    }, [removeListDialogState, handleClose]);

    return (
      <>
        <SearchTableActionAddListDialog />

        <SearchTableActionRemoveLabelDialog
          titleHeader={`Remove from List (${ids.length})`}
          title="Lists to Remove:"
          icon="list-remove"
          type={LabelType.LIST}
          table={table}
          state={removeListDialogState}
        />

        <Tooltip
          className={classnames('SearchTableActionDialogLists', className)}
          sx={SX.root}
          title={disabled ? 'Please select some records first' : undefined}
          arrow
        >
          <Stack justifyContent="flex-end" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon name="list-add" size={16} />}
              endIcon={
                !isSmall && (
                  <Icon name={open ? 'chevron-up' : 'chevron-down'} size={18} />
                )
              }
              id="basic-button"
              aria-controls={open ? 'basic-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              disabled={disabled}
              onClick={handleClick}
            >
              {!isSmall && 'Lists'}
            </Button>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <StyledActionMenuItem onClick={handleAddListClick}>
                Add/Create List
              </StyledActionMenuItem>
              <StyledActionMenuItem
                onClick={handleRemoveListClick}
                disabled={isNewTab}
              >
                Remove List
              </StyledActionMenuItem>
            </Menu>
          </Stack>
        </Tooltip>
      </>
    );
  }
);

SearchTableActionDialogLists.displayName = 'SearchTableActionDialogLists';
